import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { ClientRef } from '../../domain/models/client-ref.model';
import { IAppointmentClientLookupRepository } from '../../domain/repositories/appointment-client-lookup.repository';
import { AppointmentClientLookupRemoteDatasource } from '../datasources/remote/appointment-client-lookup.remote-datasource';
import { ClientRefMapper } from '../mappers/client-ref.mapper';

@Injectable()
export class AppointmentClientLookupRepositoryImpl implements IAppointmentClientLookupRepository {
  private readonly datasource = inject(AppointmentClientLookupRemoteDatasource);
  private readonly mapper = inject(ClientRefMapper);

  public search(term: string): Observable<readonly ClientRef[]> {
    return this.datasource.search(term).pipe(map((dto) => dto.Items.map((item) => this.mapper.toClientRef(item))));
  }

  public getById(id: string): Observable<ClientRef | null> {
    return this.datasource.getById(id).pipe(
      map((dto) => this.mapper.toClientRef(dto)),
      catchError(() => of(null)),
    );
  }

  // Seeds every requested id with null upfront so ids the backend omits (not found) still resolve
  // to a definite "unknown" entry instead of being absent from the map.
  public getByIds(ids: readonly string[]): Observable<ReadonlyMap<string, ClientRef | null>> {
    return this.datasource.getByIds(ids).pipe(
      map((dto) => {
        const result = new Map<string, ClientRef | null>(ids.map((id) => [id, null]));
        for (const item of dto.Items) {
          const clientRef = this.mapper.toClientRef(item);
          result.set(clientRef.id, clientRef);
        }
        return result;
      }),
      catchError(() => of(new Map(ids.map((id) => [id, null])))),
    );
  }
}
